import {
  EmailRequestType,
  GetEmailByIdRequest,
  SearchEmailsRequest,
  UnifiedEmailErrorCode,
  UnifiedSendEmailErrorResponse,
  UnifiedSendEmailRequest,
  UnifiedSendEmailResponse,
} from '@this+that/models';
import axios from 'axios';
import log from 'loglevel';

import { useUserCommunicationsStore } from '../store/Inbox/UserCommunications';
import { createApiWithAuth } from '../utils/axios';
import {
  HTTP_STATUS_PAYLOAD_TOO_LARGE,
  MAX_EMAIL_REQUEST_BYTES,
} from '../utils/constants/drafts';
import { EMPTY_CONTENT_PATTERNS } from '../utils/editorContentUtils';

const DRAFT_VERSION_CONFLICT_MESSAGE = 'Draft version changed on the server';
const PAYLOAD_TOO_LARGE_MESSAGE = 'Email request exceeds the payload limit';

export class DraftVersionConflictError extends Error {
  readonly currentDraftVersion?: string;

  constructor(message: string, currentDraftVersion?: string) {
    super(message);
    this.name = 'DraftVersionConflictError';
    this.currentDraftVersion = currentDraftVersion;
  }
}

export class RequestPayloadTooLargeError extends Error {
  constructor(message: string = PAYLOAD_TOO_LARGE_MESSAGE) {
    super(message);
    this.name = 'RequestPayloadTooLargeError';
  }
}

const isPayloadTooLargeResponse = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === HTTP_STATUS_PAYLOAD_TOO_LARGE;

// Detects the conflict by HTTP status or errorCode only, never by matching the
// (potentially localized/redacted) message string.
const extractDraftVersionConflict = (
  error: unknown,
): UnifiedSendEmailErrorResponse | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const body = error.response?.data?.message as
    | UnifiedSendEmailErrorResponse
    | undefined;

  const isConflict =
    error.response?.status === 409 ||
    body?.errorCode === UnifiedEmailErrorCode.DRAFT_VERSION_CONFLICT;

  return isConflict ? body : undefined;
};

const handleRequestError = (context: string, error: unknown) => {
  if (error instanceof RequestPayloadTooLargeError) {
    throw error;
  }
  if (isPayloadTooLargeResponse(error)) {
    log.warn(`Unified Email Service payload too large [${context}]`);
    throw new RequestPayloadTooLargeError();
  }

  const conflict = extractDraftVersionConflict(error);
  if (conflict) {
    throw new DraftVersionConflictError(
      conflict.message ?? DRAFT_VERSION_CONFLICT_MESSAGE,
      conflict.currentDraftVersion,
    );
  }

  log.error(`Unified Email Service Error [${context}]:`, error);
  throw error;
};

let unifiedEmailServiceApi: ReturnType<typeof createApiWithAuth> | null = null;

const getUnifiedEmailServiceApi = () => {
  if (!unifiedEmailServiceApi) {
    const baseUrl = import.meta.env.VITE_UNIFIED_EMAIL_BASE_URL;
    if (!baseUrl) {
      throw new Error('Unified Email Service URL is not set');
    }
    unifiedEmailServiceApi = createApiWithAuth(baseUrl);
  }
  return unifiedEmailServiceApi;
};

const sanitizeValue = <T>(value: T): T | undefined => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      trimmed === EMPTY_CONTENT_PATTERNS.EMPTY ||
      trimmed === EMPTY_CONTENT_PATTERNS.PARAGRAPH ||
      trimmed === EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR ||
      trimmed === EMPTY_CONTENT_PATTERNS.DIV_BR ||
      trimmed === EMPTY_CONTENT_PATTERNS.BR
    ) {
      return undefined;
    }
    return trimmed as T;
  }

  if (value === null || value === undefined) {
    return undefined;
  }

  return value;
};

export const searchEmails = async ({
  from,
  to,
  subject,
  keywords,
  isRead,
  hasAttachment,
  labels,
  after,
  before,
  maxResults,
  pageTokens,
}: SearchEmailsRequest) => {
  try {
    const { data } = await getUnifiedEmailServiceApi().post('/emails/search', {
      from,
      to,
      subject,
      keywords,
      isRead,
      hasAttachment,
      labels,
      after,
      before,
      maxResults,
      pageTokens,
    });

    const {
      message: { messages, nextPageTokens, totalCount },
    } = data;

    return { messages, nextPageTokens, totalCount };
  } catch (error) {
    return handleRequestError('searching emails', error);
  }
};

export const getEmailById = async ({
  messageId,
  provider,
  accountId,
}: GetEmailByIdRequest) => {
  const { data } = await getUnifiedEmailServiceApi().get(`/emails/get-by-id`, {
    params: {
      messageId,
      provider,
      accountId,
    },
  });
  return data;
};

const buildSendRequestBody = ({
  providerAccount,
  reqType,
  subject,
  body,
  toAddresses,
  ccAddresses,
  bccAddresses,
  messageId,
  draftId,
  draftType,
  additionalText,
  attachments,
  attachmentsToRemove,
  modifiedQuotedContent,
  expectedDraftVersion,
}: UnifiedSendEmailRequest) => {
  const isClearingExistingDraftBody =
    reqType === EmailRequestType.DRAFT &&
    Boolean(draftId) &&
    (body === EMPTY_CONTENT_PATTERNS.EMPTY ||
      body === EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR);

  return {
    providerAccount,
    reqType,
    subject: sanitizeValue(subject),
    body: isClearingExistingDraftBody ? body : sanitizeValue(body),
    // The compositor owns the full reviewed recipient list for DRAFT/SEND_DRAFT, so an
    // empty array must reach the provider as "clear this field" — never collapsed to
    // undefined ("keep existing"), or a removed Cc/Bcc/To recipient still gets the email.
    toAddresses,
    ccAddresses,
    bccAddresses,
    messageId: sanitizeValue(messageId),
    draftId,
    draftType,
    additionalText: sanitizeValue(additionalText),
    attachments: sanitizeValue(attachments),
    attachmentsToRemove: sanitizeValue(attachmentsToRemove),
    modifiedQuotedContent: sanitizeValue(modifiedQuotedContent),
    expectedDraftVersion,
  };
};

// Measures the exact JSON the request will carry, so the request never leaves for a guaranteed 413.
const assertWithinRequestLimit = (requestBody: object) => {
  const bytes = new Blob([JSON.stringify(requestBody)]).size;
  if (bytes > MAX_EMAIL_REQUEST_BYTES) {
    log.warn('Email request blocked before sending, over the payload limit', {
      bytes,
    });
    throw new RequestPayloadTooLargeError();
  }
};

export const sendEmails = async (request: UnifiedSendEmailRequest) => {
  const { reqType } = request;
  try {
    const requestBody = buildSendRequestBody(request);
    assertWithinRequestLimit(requestBody);

    const { data } = await getUnifiedEmailServiceApi().post<{
      message: UnifiedSendEmailResponse;
    }>('/emails/send', requestBody);

    const result = data.message;

    if (reqType === EmailRequestType.DRAFT && result?.draftId) {
      const { getUserCommunicationByDraftId, upsertDraft } =
        useUserCommunicationsStore.getState();

      // Skipped when the row does not exist yet: the auto-save commit that follows writes the full row.
      if (getUserCommunicationByDraftId(result.draftId)) {
        upsertDraft({
          draftId: result.draftId,
          messageId: result.messageId,
          messageRawText: result.messageRawText,
        });
      }
    }

    return data;
  } catch (error) {
    return handleRequestError('sending email', error);
  }
};
